import React from 'react'
import { Link } from '@tanstack/react-router'

import { Badge } from '@/components/ui/badge.tsx'
import {
  Card,
  CardContent,
  CardHeader,
} from '@/components/ui/card.tsx'
import { Skeleton } from '@/components/ui/skeleton.tsx'
import { DateFormat } from '@/components/date-format.tsx'
import {
  DocumentAttributeRender,
  isComplexDataType,
} from '@/features/documents/document-attribute-render.tsx'
import { useDocumentProcessingStatus } from '@/features/documents/hooks/use-document-processing-status.ts'
import { StatusBadge } from '@/features/documents/status-badge.tsx'
import type { DocumentsViewProps } from '@/features/documents/types.ts'
import { Thumbnail } from '@/features/files/thumbnail.tsx'
import { getProcessingStepText } from '@/routes/_authenticated/documents/-hooks/useGetProcessingStepDesc.ts'

export const DocumentsGrid: React.FC<DocumentsViewProps> = ({
  documents,
  uploadDateVisible = true,
  statusVisible = false,
  processingStatusVisible = false,
  leftSlot,
  onItemClick,
}) => {
  const { elapsedSecondsMap } = useDocumentProcessingStatus(
    documents,
    processingStatusVisible
  )

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {documents?.map((document) => (
        <Link
          key={document.id}
          to="/documents/$documentId"
          params={{ documentId: document.id }}
          onClick={(e) => onItemClick?.(e, document)}
          className="group"
        >
          <Card className="h-full transition-shadow hover:shadow-lg">
            {leftSlot?.(document)}
            <CardHeader className="p-0">
              <div className="relative flex h-[240px] w-full items-center justify-center overflow-hidden rounded-t-lg bg-muted">
                {document.jobs?.[0]?.thumbnails_processing?.status ===
                'completed' ? (
                  <Thumbnail
                    className="max-h-[240px] max-w-full object-contain transition-transform group-hover:scale-105"
                    fileId={document.file_id}
                  />
                ) : (
                  <Skeleton className="h-[240px] w-full" />
                )}
                {statusVisible && (
                  <div className="absolute right-2 top-2">
                    <StatusBadge document={document} />
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-4">
              <h3 className="mb-2 line-clamp-2 text-sm font-semibold leading-tight">
                {document.name}
              </h3>

              {uploadDateVisible && (
                <p className="mb-3 text-xs text-muted-foreground">
                  Caricato: <DateFormat date={document.updated_at} />
                </p>
              )}

              {processingStatusVisible && (
                <div className="mb-3 flex flex-col gap-1 text-xs text-muted-foreground">
                  {elapsedSecondsMap.get(document.id) && (
                    <span>{elapsedSecondsMap.get(document.id)} secondi</span>
                  )}
                  {getProcessingStepText(document) && (
                    <span>{getProcessingStepText(document)}</span>
                  )}
                </div>
              )}

              {document.attributes && document.attributes.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {document.attributes
                    .slice(0, 3)
                    .map((documentAttribute, index) => (
                      <Badge
                        key={index}
                        variant="outline"
                        className="max-w-full overflow-hidden text-ellipsis text-xs"
                        title={`${documentAttribute.attribute.name}${
                          !isComplexDataType(
                            documentAttribute.attribute.json_schema
                          )
                            ? `: ${String(documentAttribute.value)}`
                            : ''
                        }`}
                      >
                        <span className="inline-block max-w-full truncate">
                          {documentAttribute.attribute.name}
                          {!isComplexDataType(
                            documentAttribute.attribute.json_schema
                          ) && (
                            <>
                              :{' '}
                              <DocumentAttributeRender
                                attributeJsonSchema={
                                  documentAttribute.attribute.json_schema
                                }
                                value={documentAttribute.value}
                              />
                            </>
                          )}
                        </span>
                      </Badge>
                    ))}
                  {document.attributes.length > 3 && (
                    <Badge variant="outline" className="text-xs">
                      +{document.attributes.length - 3} altri
                    </Badge>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </Link>
      ))}
    </div>
  )
}
